import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { LoadingView } from 'day8-react';
import { FormWrap, Input, TelephoneInput, Button, toast } from 'day8-ui';
import { ReCaptcha } from 'react-recaptcha-google';
import { apiHost } from '../../config';
import { COMPANIES } from '../../constants';
import { signupUser, validateUser } from '../../actions/user';
import { withActiveCompany } from '../../context/ActiveCompanyContext';
import history from '../../history';

class Signup extends Component {
  constructor(props) {
    super(props);

    this.state = {
      email: '',
      phone: '',
      firstName: '',
      lastName: '',
      submitting: false
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onLoadRecaptcha = this.onLoadRecaptcha.bind(this);
    this.verifyCallback = this.verifyCallback.bind(this);
    this.onSuccess = this.onSuccess.bind(this);
  }

  componentDidMount() {
    this.getToken();
    this.getCountryCode();
    this.onLoadRecaptcha();
  }

  onLoadRecaptcha(e) {
    if (e) e.preventDefault();
    if (this.captchaDemo) {
      this.captchaDemo.reset();
      this.captchaDemo.execute();
    }
  }

  onSuccess() {
    const { location } = this.props;
    const returnTo =
      location && location.state && location.state.returnTo;
    return returnTo ? history.push(returnTo) : history.push('/account');
  }

  async getToken() {
    const result = await fetch(`${apiHost}/api/authentication/signup`, {
      credentials: 'same-origin'
    });
    const json = await result.json();
    const token = json.data && json.data._token;
    this.setState({ token });
  }

  async getCountryCode() {
    const result = await fetch(`${apiHost}/api/account/country-code`, {
      credentials: 'same-origin'
    });
    const json = await result.json();
    const countrycode = json.data && json.data.countrycode;
    if (countrycode) {
      this.setState({ countrycode: countrycode?.toUpperCase() });
    }
  }

  async handleSubmit() {
    this.setState({ submitting: true });
    const {
      email, phone, firstName, token, lastName
    } = this.state;
    const {
      signupUser, validateUser, shouldValidateUser, isYachtsAndFriends
    } = this.props;
    const result = await signupUser(
      email,
      phone,
      firstName,
      token,
      lastName,
      isYachtsAndFriends ? COMPANIES.YNF : COMPANIES.DAY8
    );

    if (result.status === 'success') {
      toast.success("Login successful");
      if (shouldValidateUser) {
        validateUser(this.onSuccess);
      } else {
        this.onSuccess();
      }
    }
    const errors = {};
    if (result?.errors) {
      toast.error("Please check your details and try again");
      Object.keys(result.errors).forEach(error => {
        errors[error] = {
          notificationType: 'error',
          notificationMessage: Array.isArray(result.errors[error]) ? result.errors[error][0] : Object.entries(result.errors[error])[0][1][0], // this is how nested those error messages are coming from Symfony...
          showNotification: true
        };
      });
    }
    this.setState({ submitting: false, errors });
  }

  verifyCallback(recaptchaToken) {
    if (recaptchaToken && this.state.email) {
      this.handleSubmit();
    }
  }

  render() {
    const {
      submitting,
      errors,
      email,
      firstName,
      countrycode,
      phone,
      lastName
    } = this.state;
    const { user, loginTo } = this.props;

    if (submitting) {
      return <LoadingView title="Creating your profile.." />;
    }

    if (user && user.id) {
      return <h2>Already logged in</h2>;
    }
    return (
      <div>
        <FormWrap onSubmit={this.onLoadRecaptcha} loading={submitting}>
          <div className='dui-flex dui-flex-col dui-gap-4'>
            <div className='dui-grid sm:dui-grid-cols-2 dui-gap-4'>
              <Input
                id="firstName"
                className='dui-w-full'
                type="firstName"
                label="First name"
                placeholder="First name"
                value={firstName}
                onChange={e => this.setState({ firstName: e.target.value })}
                errorMessage={errors && errors.firstName?.notificationMessage}
                required
              />
              <Input
                id="lastName"
                className='dui-w-full'
                type="lastName"
                label="Last name"
                placeholder="Last name"
                value={lastName}
                onChange={e => this.setState({ lastName: e.target.value })}
                errorMessage={errors && errors.lastName?.notificationMessage}
                required
              />
            </div>
            <Input
              id="email"
              label="Email"
              className='dui-w-full'
              placeholder="email.address@example.com"
              type="email"
              value={email}
              onChange={e => this.setState({ email: e.target.value })}
              errorMessage={errors && errors.email?.notificationMessage}
              required
            />
            <TelephoneInput
              inputContainerClassName='dui-flex-grow'
              className='dui-w-full'
              inputClassName='dui-w-full'
              onChange={number => this.setState({ phone: number })}
              defaultCountry={countrycode}
              label="Phone number"
              errorMessage={errors && errors.phone?.notificationMessage}
              value={phone}
              required
            />
            <p className='dui-text-gray-400 dui-text-sm'>Create an account and learn more about the product from our team.</p>
            <Button
              disabled={submitting}
              type="submit"
              className="dui-w-full"
            >
          Create my account
            </Button>
            <p className="dui-text-gray-400">
              This site is protected by reCAPTCHA and the Google
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                {' '}
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>{' '}
              apply.
            </p>
            {window.grecaptcha ? (
              <ReCaptcha
                ref={el => { this.captchaDemo = el; }}
                size="invisible"
                render="explicit"
                sitekey="6Lc9GsIUAAAAABpO3JLwFv9dmkC1lOQDNRzwGZ-0"
                onloadCallback={this.onLoadRecaptcha}
                verifyCallback={this.verifyCallback}
              />
            ) : null}
          </div>
        </FormWrap>
        <p className="dui-text-gray-400 dui-text-sm dui-mt-4">
      Already have an account?{' '}
          <Link className="dui-underline dui-font-bold dui-text-primary-950" to={loginTo || 'login'}>
            Log in
          </Link>
        </p>
      </div>
    );
  }
}

Signup.propTypes = {
  signupUser: PropTypes.func.isRequired,
  user: PropTypes.object,
  loginTo: PropTypes.object,
  location: PropTypes.object,
  shouldValidateUser: PropTypes.bool,
  validateUser: PropTypes.func,
  isYachtsAndFriends: PropTypes.bool
};

const mapStateToProps = store => ({
  user: store.user.loggedUser
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      signupUser,
      validateUser
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(withActiveCompany(Signup));
